import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Bell, FileBarChart, Trash2, FileUp } from "lucide-react";
import { api, formatApiError } from "../api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import ExtractionPanel from "../components/ExtractionPanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function DashboardPage() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saved, setSaved] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [reports, setReports] = useState([]);
  const [billing, setBilling] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadExtraction, setUploadExtraction] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [mfa, setMfa] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [delPass, setDelPass] = useState("");
  const [acctMsg, setAcctMsg] = useState(null);

  const setupMfa = async () => {
    try { const { data } = await api.post("/auth/mfa/setup"); setMfa(data); }
    catch (e) { setAcctMsg(formatApiError(e)); }
  };
  const enableMfa = async () => {
    try {
      await api.post("/auth/mfa/enable", { code: mfaCode });
      setMfa(null);
      setAcctMsg(t("account.mfa.enabled"));
      window.location.reload();
    } catch (e) { setAcctMsg(formatApiError(e)); }
  };
  const exportData = async () => {
    const r = await api.get("/gdpr/export", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = "plangmina-export.json";
    a.click();
  };
  const deleteAccount = async () => {
    try {
      await api.delete("/gdpr/account", { data: { password: delPass } });
      window.location.href = "/";
    } catch (e) { setAcctMsg(formatApiError(e)); }
  };
  const cancelSub = async () => {
    try {
      const r = await api.post("/billing/subscription/cancel");
      setAcctMsg(`${t("dash.plan.cancel_note")}: ${new Date(r.data.access_until).toLocaleDateString("pl-PL")}`);
      const b = await api.get("/billing/me");
      setBilling(b.data);
    } catch (e) { setAcctMsg(formatApiError(e)); }
  };
  const resendVerification = async () => {
    await api.post("/auth/resend-verification");
    setAcctMsg("OK");
  };

  useEffect(() => {
    if (user === null) return;
    if (user === false) { navigate("/logowanie"); return; }
    api.get("/saved-parcels").then((r) => setSaved(r.data.results)).catch(() => setSaved([]));
    api.get("/alerts").then((r) => setAlerts(r.data.results)).catch(() => {});
    api.get("/reports").then((r) => setReports(r.data.results)).catch(() => {});
    api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => setBilling(null));
  }, [user, navigate]);

  if (!user || saved === null) return <div className="p-20 text-center text-slate-500">{t("common.loading")}</div>;

  const remove = async (id) => {
    await api.delete(`/saved-parcels/${id}`);
    setSaved((s) => s.filter((x) => x.id !== id));
  };

  const uploadAndAnalyze = async () => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    setUploadExtraction(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/documents/upload", fd);
      const { data: ex } = await api.post(`/documents/${up.id}/analyze`);
      setUploadExtraction(ex);
    } catch (e) {
      setUploadError(formatApiError(e));
    }
    setUploading(false);
  };

  return (
    <div data-testid="dashboard-page" className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <h1 className="font-heading text-3xl font-extrabold tracking-tight">{t("dash.title")}</h1>
      <p className="mt-1 text-sm text-slate-500">{user.email} · {t("auth.role")}: <span className="font-semibold uppercase">{user.role}</span></p>

      {user.email_verified === false && (
        <div data-testid="email-unverified-banner" className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <p className="text-sm text-amber-900">{t("account.email.unverified")}</p>
          <Button data-testid="resend-verification-button" variant="outline" size="sm" onClick={resendVerification}>
            {t("account.email.resend")}
          </Button>
        </div>
      )}

      <Link to="/workspace" data-testid="dash-workspace-card" className="mt-6 flex items-center justify-between gap-4 rounded-xl border-2 border-slate-900 bg-white p-5 transition-colors hover:bg-slate-50">
        <div>
          <p className="font-heading text-lg font-semibold">{t("dash.workspace.title")}</p>
          <p className="mt-0.5 text-sm text-slate-500">{t("dash.workspace.desc")}</p>
        </div>
        <span className="shrink-0 rounded-lg bg-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white">{t("dash.workspace.cta")}</span>
      </Link>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <section className="min-w-0 lg:col-span-2">
          <h2 className="font-heading text-lg font-semibold">{t("dash.saved")}</h2>
          {saved.length === 0 ? (
            <div className="mt-4 rounded-xl border border-dashed bg-white p-10 text-center">
              <p className="text-sm text-slate-500">{t("dash.empty")}</p>
              <Button data-testid="dash-find-parcel-button" className="mt-4 bg-[#DC143C] text-white hover:bg-[#B91C1C]" onClick={() => navigate("/")}>
                {t("dash.find")}
              </Button>
            </div>
          ) : (
            <div data-testid="saved-parcels-table" className="mt-4 overflow-x-auto rounded-xl border bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">{t("pro.table.parcel")}</th>
                    <th className="px-4 py-3">{t("pro.table.gmina")}</th>
                    <th className="px-4 py-3">{t("pro.table.plan")}</th>
                    <th className="hidden px-4 py-3 md:table-cell">{t("dash.lastChecked")}</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {saved.map((s) => (
                    <tr key={s.id} data-testid={`saved-parcel-row-${s.parcel_id}`}>
                      <td className="px-4 py-3">
                        <Link to={`/dzialka/${String(s.parcel_id).replaceAll("/", "~")}`} className="font-mono-data font-semibold text-[#DC143C] hover:underline">
                          {s.parcel.full_number}
                        </Link>
                        {s.data_kind === "official" && (
                          <span data-testid={`saved-official-badge-${s.id}`} className="ml-2 rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-emerald-700">
                            {t("data.official")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{s.parcel.gmina_name}</td>
                      <td className="px-4 py-3"><StatusBadge status={s.parcel.plan_ogolny_status} label={t(`status.${s.parcel.plan_ogolny_status}`)} /></td>
                      <td className="hidden px-4 py-3 text-xs text-slate-600 md:table-cell">{new Date(s.last_checked).toLocaleDateString("pl-PL")}</td>
                      <td className="px-4 py-3 text-right">
                        <button data-testid={`remove-saved-${s.id}`} onClick={() => remove(s.id)} className="text-slate-400 transition-colors hover:text-rose-600">
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h2 className="mt-8 font-heading text-lg font-semibold">{t("dash.reports")}</h2>
          <div className="mt-4 space-y-2">
            {reports.length === 0 && <p className="text-sm text-slate-500">—</p>}
            {reports.map((r) => (
              <Link
                key={r.id}
                to={`/raport/${r.id}`}
                data-testid={`report-link-${r.id}`}
                className="flex items-center justify-between rounded-lg border bg-white px-4 py-3 transition-colors hover:border-[#DC143C]/40"
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <FileBarChart size={15} className="text-[#DC143C]" />
                  {r.data?.parcel_identification?.number}
                </span>
                <span className="text-xs text-slate-600">{new Date(r.created_at).toLocaleString("pl-PL")}</span>
              </Link>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold">{t("dash.alerts")}</h2>
          <div data-testid="planning-watch-feed" className="mt-4 space-y-3">
            {alerts.length === 0 && <p className="text-sm text-slate-500">—</p>}
            {alerts.map((a) => (
              <div
                key={a.id}
                data-testid={`alert-${a.id}`}
                className={`rounded-xl border p-4 ${a.read ? "bg-white" : "border-amber-300 bg-amber-50"}`}
              >
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-amber-700">
                  <Bell size={12} /> {a.type.replaceAll("_", " ")}
                </p>
                <p className="mt-1.5 text-sm text-slate-800">{lang === "pl" ? a.message_pl : a.message_en}</p>
                {a.type === "watch_change" && (
                  <p data-testid={`alert-diff-${a.id}`} className="mt-1 text-xs text-slate-500">
                    {a.previous_value} → {a.new_value} · {a.source_authority}
                  </p>
                )}
                <p className="mt-2 text-[11px] text-slate-600">{new Date(a.created_at).toLocaleString("pl-PL")}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="mt-10" data-testid="dash-plan-section">
        <h2 className="font-heading text-lg font-semibold">{t("dash.plan.title")}</h2>
        <div className="mt-4 rounded-xl border bg-white p-5">
          {billing && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p data-testid="dash-plan-name" className="text-sm">
                  {t("dash.plan.current")}: <strong>{lang === "pl" ? billing.plan?.name_pl : billing.plan?.name_en}</strong>
                  {billing.provider_mode === "test" && (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800">TEST</span>
                  )}
                </p>
                <Button data-testid="dash-change-plan" variant="outline" size="sm" onClick={() => navigate("/cennik")}>
                  {t("dash.plan.change")}
                </Button>
              </div>
              <p data-testid="dash-usage" className="mt-2 text-xs text-slate-600">
                {t("dash.plan.reports")}: {billing.usage.reports_generated}/{billing.entitlements.reports_per_month === -1 ? "∞" : billing.entitlements.reports_per_month}
                {" · "}
                {t("dash.plan.saved")}: {billing.usage.saved_parcels}/{billing.entitlements.saved_parcels_limit === -1 ? "∞" : billing.entitlements.saved_parcels_limit}
              </p>
              {billing.subscription && (
                <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
                  <p>
                    {billing.subscription.cancel_at_period_end
                      ? <>{t("dash.plan.cancel_note")}: <strong>{new Date(billing.subscription.current_period_end).toLocaleDateString("pl-PL")}</strong></>
                      : <>{t("dash.plan.active_until")}: <strong>{new Date(billing.subscription.current_period_end).toLocaleDateString("pl-PL")}</strong></>}
                  </p>
                  {!billing.subscription.cancel_at_period_end && (
                    <Button data-testid="dash-cancel-sub" variant="outline" size="sm" className="mt-2" onClick={cancelSub}>
                      {t("dash.plan.cancel")}
                    </Button>
                  )}
                </div>
              )}
              <h3 className="mt-4 text-xs font-bold uppercase tracking-wide text-slate-500">{t("dash.plan.purchases")}</h3>
              <div data-testid="dash-purchases" className="mt-2 space-y-1.5">
                {billing.purchases.length === 0 && <p className="text-xs text-slate-500">{t("dash.plan.none")}</p>}
                {billing.purchases.map((p) => (
                  <p key={p.id} data-testid={`dash-purchase-${p.id}`} className="flex justify-between text-xs text-slate-600">
                    <span className="font-mono-data">{p.receipt_reference}</span>
                    <span>{(p.amount_grosze / 100).toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł · {p.status}</span>
                  </p>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="mt-10" data-testid="doc-upload-section">
        <h2 className="font-heading text-lg font-semibold">{t("docs.upload.title")}</h2>
        <div className="mt-4 rounded-xl border bg-white p-5">
          <p className="text-xs text-slate-600">{t("docs.upload.hint")}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input
              type="file"
              data-testid="doc-upload-input"
              accept=".pdf,.txt"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm text-slate-600 file:mr-3 file:rounded-md file:border file:px-3 file:py-1.5 file:text-sm file:font-medium file:hover:bg-slate-50"
            />
            <Button
              data-testid="doc-upload-submit"
              disabled={!file || uploading}
              onClick={uploadAndAnalyze}
              className="bg-indigo-600 text-white hover:bg-indigo-700"
            >
              <FileUp size={15} className="mr-2" />
              {uploading ? t("ai.analyzing") : t("docs.upload.button")}
            </Button>
          </div>
          {uploadError && (
            <p data-testid="doc-upload-error" className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {uploadError}
            </p>
          )}
          <ExtractionPanel extraction={uploadExtraction} t={t} lang={lang} />
        </div>
      </section>

      <section className="mt-10" data-testid="account-security-section">
        <h2 className="font-heading text-lg font-semibold">{t("account.title")}</h2>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          {(user.role === "admin" || user.role === "professional") && (
            <div className="rounded-xl border bg-white p-5">
              <h3 className="text-sm font-bold">{t("account.mfa.title")}</h3>
              {user.mfa_enabled ? (
                <p data-testid="mfa-enabled-label" className="mt-2 text-sm font-semibold text-emerald-700">{t("account.mfa.enabled")}</p>
              ) : (
                <>
                  {user.role === "admin" && (
                    <p className="mt-2 text-xs font-medium text-amber-700">{t("account.mfa.required")}</p>
                  )}
                  {!mfa ? (
                    <Button data-testid="mfa-setup-button" variant="outline" size="sm" className="mt-3" onClick={setupMfa}>
                      {t("account.mfa.start")}
                    </Button>
                  ) : (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs text-slate-600">{t("account.mfa.secret")}:</p>
                      <p data-testid="mfa-secret" className="break-all rounded bg-slate-100 p-2 font-mono-data text-xs">{mfa.secret}</p>
                      <p className="break-all text-[10px] text-slate-500">{mfa.otpauth_url}</p>
                      <div className="flex gap-2">
                        <Input data-testid="mfa-code-input" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder={t("account.mfa.code")} className="h-9 w-36" />
                        <Button data-testid="mfa-enable-button" size="sm" onClick={enableMfa} className="bg-[#DC143C] text-white hover:bg-[#B91C1C]">
                          {t("account.mfa.enable")}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          <div className="rounded-xl border bg-white p-5">
            <h3 className="text-sm font-bold">GDPR / RODO</h3>
            <div className="mt-3">
              <Button data-testid="gdpr-export-button" variant="outline" size="sm" onClick={exportData}>
                {t("account.export")}
              </Button>
            </div>
            <p className="mt-4 text-xs text-slate-600">{t("account.delete.confirm")}</p>
            <div className="mt-2 flex gap-2">
              <Input data-testid="delete-password-input" type="password" value={delPass} onChange={(e) => setDelPass(e.target.value)} placeholder={t("auth.password")} className="h-9 w-48" />
              <Button data-testid="delete-account-button" variant="outline" size="sm" onClick={deleteAccount} className="border-rose-300 text-rose-700 hover:bg-rose-50">
                {t("account.delete")}
              </Button>
            </div>
          </div>
        </div>
        {acctMsg && <p data-testid="account-message" className="mt-3 text-sm text-slate-600">{acctMsg}</p>}
      </section>
    </div>
  );
}
